import React, { useCallback, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';

const DEFAULT_OFFSET = 100;

const Placeholder = styled.div<{
  imageHeight?: string;
  imageWidth?: string;
  backgroundColor?: string;
}>`
  background-color: ${({ backgroundColor, theme }) =>
    backgroundColor || theme.background.secondary};
  height: ${({ imageHeight }) => (imageHeight ? `${imageHeight}px` : '')};
  width: ${({ imageWidth }) => (imageWidth ? `${imageWidth}px` : '')};
`;

type ImageProps = {
  src: string;
  alt: string;
  ariaLabel?: string;
  height?: string;
  width?: string;
  lazy?: boolean;
  offset?: number;
  throttle?: number;
  backgroundColor?: string;
  role?: string;
};

const Image = ({
  src,
  alt,
  ariaLabel,
  height,
  backgroundColor,
  width = 'auto',
  lazy = true,
  offset = DEFAULT_OFFSET,
  throttle = 0,
  role = 'img',
  ...other
}: ImageProps): JSX.Element => {
  const imageRef = useRef<HTMLElement | null>(null);
  const observerRef = useRef<IntersectionObserver | null>(null);
  const [isLoaded, setLoaded] = useState<boolean>(false);

  const load = useCallback(() => {
    setTimeout(() => {
      setLoaded(true);
    }, throttle);
  }, [throttle]);

  useEffect(() => {
    if (lazy && typeof IntersectionObserver === 'function') {
      observerRef.current = new IntersectionObserver(
        (entries) => {
          const image = entries[0];
          if (image.isIntersecting) {
            load();
            observerRef.current?.disconnect();
          }
        },
        {
          root: null,
          rootMargin: `0px 0px ${offset}px 0px`,
          threshold: 0.1,
        }
      );

      if (imageRef.current) {
        observerRef.current.observe(imageRef.current);
      }
    } else {
      load();
    }

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, [lazy, offset, load]);

  const img = (
    <img
      alt={alt}
      aria-label={ariaLabel}
      height={height}
      src={src}
      width={width}
      role={role}
      {...other}
    />
  );

  if (!isLoaded && lazy) {
    return (
      <Placeholder
        ref={(el) => {
          if (el) imageRef.current = el;
        }}
        imageWidth={width}
        imageHeight={height}
        backgroundColor={backgroundColor}
        {...other}
      >
        <noscript>{img}</noscript>
      </Placeholder>
    );
  }

  return img;
};

export default Image;
